/**
 * Clean pitch-shifted copies of a single recording.
 *
 * Playing one recording far above its own pitch by speeding it up folds the
 * highs back down as harsh aliasing: the browser's playback-rate
 * interpolation does not band-limit. Copies prepared 6, 12, 18 and 24
 * semitones up with CookUp's band-limited (windowed-sinc) resampler remove
 * that. A note then plays from the nearest copy, so the browser only shifts
 * it by about ±3 semitones. Notes below the root keep the original:
 * slowing a recording down does far less harm. The copies are shorter than
 * the original, so all four together add about 1.8x its memory.
 */
export const PITCH_COPY_STEPS = [6, 12, 18, 24];
export const PITCH_COPY_MAX_SECONDS = 30;
// Within this many semitones of the original, plain playback-rate shifting is clean enough.
const CLEAN_SHIFT = 3;

/** The prepared copy (semitones above the original) that a shift should play from, or 0 for the original. */
export function pitchCopyFor(shiftSemitones) {
  if (!(shiftSemitones > CLEAN_SHIFT)) return 0;
  const nearest = Math.round(shiftSemitones / 6) * 6;
  return Math.min(PITCH_COPY_STEPS[PITCH_COPY_STEPS.length - 1], Math.max(PITCH_COPY_STEPS[0], nearest));
}

export const pitchCopyKey = (bufferKey, semitones) => `${bufferKey}|+${semitones}`;

/** Whether a recording is worth preparing copies for (bounded memory). */
export const wantsPitchCopies = (buffer) => Number(buffer?.duration) > 0 && Number(buffer.duration) <= PITCH_COPY_MAX_SECONDS;
